import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#DF4728",
  dark: "#19242F",
  green: "#2A9D67",
};

const PRESETS = [
  {
    key: "complementaria",
    label: "Dedicación complementaria",
    clients: 3,
    serviceCount: 1,
    description: "Un comienzo acotado para aprender el proceso y acompañar pocos negocios.",
  },
  {
    key: "relevante",
    label: "Ingreso relevante",
    clients: 8,
    serviceCount: 2,
    description: "Una cartera en crecimiento con proyectos que combinan más de un servicio.",
  },
  {
    key: "principal",
    label: "Dedicación principal",
    clients: 15,
    serviceCount: 3,
    description: "Una referencia comercial sostenida, con seguimiento frecuente de varios clientes.",
  },
];

function finiteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function roundMoney(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function normalizedCurrency(value) {
  return String(value || "ARS").trim().toUpperCase() || "ARS";
}

function percentLabel(value) {
  const parsed = finiteNumber(value);
  if (parsed === null) return "—";

  return `${new Intl.NumberFormat("es-AR", {
    maximumFractionDigits: 2,
  }).format(parsed)}%`;
}

function moneyLabel(value, currency) {
  const parsed = finiteNumber(value);
  if (parsed === null) return "—";

  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: normalizedCurrency(currency),
      maximumFractionDigits: 0,
    }).format(parsed);
  } catch {
    return `${new Intl.NumberFormat("es-AR", {
      maximumFractionDigits: 0,
    }).format(parsed)} ${normalizedCurrency(currency)}`;
  }
}

function validServices(services) {
  return (Array.isArray(services) ? services : [])
    .map((service) => ({
      ...service,
      code: String(service?.code || "").trim().toLowerCase(),
      name: String(service?.name || "").trim(),
      monthlyPrice: finiteNumber(service?.monthlyPrice),
      currency: normalizedCurrency(service?.currency),
      requiredServiceCodes: (Array.isArray(service?.requiredServiceCodes)
        ? service.requiredServiceCodes
        : [])
        .map((code) => String(code || "").trim().toLowerCase())
        .filter(Boolean),
    }))
    .filter(
      (service) =>
        service.code &&
        service.name &&
        service.monthlyPrice !== null &&
        service.monthlyPrice >= 0,
    );
}

function serviceMapFor(services) {
  return new Map(services.map((service) => [service.code, service]));
}

function expandDependencies(codes, servicesByCode) {
  const expanded = new Set(codes);
  const pending = [...expanded];

  while (pending.length) {
    const currentCode = pending.pop();
    const service = servicesByCode.get(currentCode);

    for (const requiredCode of service?.requiredServiceCodes || []) {
      if (!servicesByCode.has(requiredCode) || expanded.has(requiredCode)) continue;
      expanded.add(requiredCode);
      pending.push(requiredCode);
    }
  }

  return expanded;
}

function removeServiceAndDependents(code, selectedCodes, servicesByCode) {
  const remaining = new Set(selectedCodes);
  remaining.delete(code);

  let changed = true;
  while (changed) {
    changed = false;

    for (const selectedCode of [...remaining]) {
      const service = servicesByCode.get(selectedCode);
      if ((service?.requiredServiceCodes || []).some((requiredCode) => !remaining.has(requiredCode))) {
        remaining.delete(selectedCode);
        changed = true;
      }
    }
  }

  return remaining;
}

function preferredPresetCodes(services, serviceCount) {
  const servicesByCode = serviceMapFor(services);
  const paidServices = services
    .filter((service) => service.monthlyPrice > 0)
    .sort((left, right) => {
      if (left.recommended !== right.recommended) return left.recommended ? -1 : 1;
      if (left.monthlyPrice !== right.monthlyPrice) {
        return left.monthlyPrice - right.monthlyPrice;
      }
      return left.name.localeCompare(right.name, "es");
    });
  const candidates = paidServices.length ? paidServices : services;
  const initialCodes = candidates.slice(0, Math.max(1, serviceCount)).map((service) => service.code);

  return [...expandDependencies(initialCodes, servicesByCode)];
}

function totalsByCurrency(services, clients = 1) {
  const multiplier = Math.max(0, Number(clients || 0));
  const totals = new Map();

  for (const service of services) {
    const currency = normalizedCurrency(service.currency);
    totals.set(
      currency,
      roundMoney(Number(totals.get(currency) || 0) + Number(service.monthlyPrice || 0) * multiplier),
    );
  }

  return [...totals.entries()]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((left, right) => left.currency.localeCompare(right.currency));
}

function commissionTotals(totals, commissionPercent) {
  const parsedPercent = finiteNumber(commissionPercent);
  if (parsedPercent === null) return [];

  return totals.map(({ currency, amount }) => ({
    currency,
    amount: roundMoney((amount * parsedPercent) / 100),
  }));
}

function programLevels(program) {
  const levels = Array.isArray(program?.levelBehavior?.levels)
    ? program.levelBehavior.levels
    : [];

  return levels
    .map((level) => ({
      level: Math.max(1, Math.trunc(Number(level?.level || 0))),
      entitlementPercent: finiteNumber(level?.directCommissionPercent),
    }))
    .filter((level) => level.entitlementPercent !== null)
    .sort((left, right) => left.level - right.level);
}

function MoneyLines({ values, emphasis = false, emptyLabel = "Sin monto disponible" }) {
  if (!values.length) {
    return (
      <Typography sx={{ color: "rgba(25,36,47,.48)", fontWeight: 750, fontSize: ".9rem" }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25}>
      {values.map(({ currency, amount }) => (
        <Typography
          key={currency}
          sx={{
            color: emphasis ? BRAND.orange : BRAND.dark,
            fontWeight: 950,
            letterSpacing: "-.025em",
            lineHeight: 1.15,
            fontSize: emphasis ? { xs: "1.45rem", sm: "1.7rem" } : "1.2rem",
          }}
        >
          {moneyLabel(amount, currency)}
        </Typography>
      ))}
    </Stack>
  );
}

function MetricCard({ icon: Icon, eyebrow, children, detail }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        minHeight: 168,
        borderRadius: 3.8,
        border: "1px solid rgba(25,36,47,.09)",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.7,
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(243,90,58,.1)",
          color: BRAND.orange,
        }}
      >
        <Icon sx={{ fontSize: 23 }} />
      </Box>
      <Typography
        sx={{
          mt: 1.5,
          mb: 0.65,
          color: "rgba(25,36,47,.55)",
          fontWeight: 850,
          fontSize: ".76rem",
          letterSpacing: ".055em",
          textTransform: "uppercase",
        }}
      >
        {eyebrow}
      </Typography>
      {children}
      <Typography sx={{ mt: 0.85, color: "rgba(25,36,47,.55)", fontSize: ".78rem", lineHeight: 1.45 }}>
        {detail}
      </Typography>
    </Paper>
  );
}

function ScenarioCard({ preset, services, program, onApply, active = false }) {
  const codes = preferredPresetCodes(services, preset.serviceCount);
  const selectedServices = services.filter((service) => codes.includes(service.code));
  const monthlyVolume = totalsByCurrency(selectedServices, preset.clients);
  const directCommission = commissionTotals(monthlyVolume, program?.directCommissionPercent);
  const programIsActive = program?.status === "active" && program?.enabled === true;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        height: "100%",
        borderRadius: 3.8,
        border: active
          ? "1px solid rgba(243,90,58,.4)"
          : "1px solid rgba(25,36,47,.09)",
        bgcolor: active ? "#FFF8F5" : "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.2 }}>
        {preset.label}
      </Typography>
      <Typography sx={{ mt: 0.75, color: "rgba(25,36,47,.58)", fontSize: ".8rem", lineHeight: 1.5 }}>
        {preset.description}
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.7} sx={{ mt: 1.5 }}>
        <Chip size="small" label={`${preset.clients} clientes`} sx={{ fontWeight: 800 }} />
        <Chip
          size="small"
          label={`${selectedServices.length} ${selectedServices.length === 1 ? "servicio" : "servicios"}`}
          sx={{ fontWeight: 800 }}
        />
      </Stack>
      <Typography sx={{ mt: 1.7, color: "rgba(25,36,47,.5)", fontSize: ".72rem", fontWeight: 850, textTransform: "uppercase" }}>
        Volumen mensual estimado
      </Typography>
      <MoneyLines values={monthlyVolume} />
      <Typography sx={{ mt: 1.35, color: "rgba(25,36,47,.5)", fontSize: ".72rem", fontWeight: 850, textTransform: "uppercase" }}>
        Comisión directa estimada
      </Typography>
      <MoneyLines
        values={programIsActive ? directCommission : []}
        emptyLabel="No disponible con la política actual"
      />
      <Button
        onClick={() => onApply(preset)}
        variant="outlined"
        sx={{
          mt: "auto",
          pt: 1.1,
          minHeight: 42,
          borderRadius: 999,
          color: BRAND.dark,
          borderColor: "rgba(25,36,47,.2)",
          textTransform: "none",
          fontWeight: 900,
          "&:hover": { borderColor: BRAND.dark, bgcolor: "rgba(25,36,47,.03)" },
        }}
      >
        Usar este ejemplo
      </Button>
    </Paper>
  );
}

export default function SellerIncomeSimulator({ services: sourceServices, program, loading = false }) {
  const services = React.useMemo(() => validServices(sourceServices), [sourceServices]);
  const servicesByCode = React.useMemo(() => serviceMapFor(services), [services]);
  const catalogSignature = React.useMemo(
    () => services.map((service) => `${service.code}:${service.monthlyPrice}:${service.currency}`).join("|"),
    [services],
  );
  const [clients, setClients] = React.useState(PRESETS[0].clients);
  const [selectedCodes, setSelectedCodes] = React.useState([]);
  const [activePreset, setActivePreset] = React.useState(PRESETS[0].key);

  const applyPreset = React.useCallback(
    (preset) => {
      setClients(preset.clients);
      setSelectedCodes(preferredPresetCodes(services, preset.serviceCount));
      setActivePreset(preset.key);
    },
    [services],
  );

  React.useEffect(() => {
    if (!services.length) {
      setSelectedCodes([]);
      return;
    }

    setClients(PRESETS[0].clients);
    setSelectedCodes(preferredPresetCodes(services, PRESETS[0].serviceCount));
    setActivePreset(PRESETS[0].key);
  }, [catalogSignature, services]); // Se reinicia sólo cuando cambia el catálogo público real.

  const toggleService = (serviceCode) => {
    setActivePreset("");
    setSelectedCodes((currentCodes) => {
      const current = new Set(currentCodes);
      if (current.has(serviceCode)) {
        return [...removeServiceAndDependents(serviceCode, current, servicesByCode)];
      }

      current.add(serviceCode);
      return [...expandDependencies(current, servicesByCode)];
    });
  };

  const selectedServices = React.useMemo(
    () => services.filter((service) => selectedCodes.includes(service.code)),
    [selectedCodes, services],
  );
  const perBusiness = React.useMemo(
    () => totalsByCurrency(selectedServices, 1),
    [selectedServices],
  );
  const monthlyVolume = React.useMemo(
    () => totalsByCurrency(selectedServices, clients),
    [clients, selectedServices],
  );
  const levels = React.useMemo(() => programLevels(program), [program]);
  const directPercent = finiteNumber(program?.directCommissionPercent);
  const directCommission = React.useMemo(
    () => commissionTotals(monthlyVolume, directPercent),
    [directPercent, monthlyVolume],
  );
  const programIsActive = program?.status === "active" && program?.enabled === true;
  const hasPrices = services.length > 0;
  const hasSelection = selectedServices.length > 0;

  return (
    <Box sx={{ mt: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 800 }}>
        <Typography sx={{ color: BRAND.orange, fontWeight: 900, letterSpacing: ".1em", textTransform: "uppercase", fontSize: ".76rem" }}>
          Simulador de ingresos
        </Typography>
        <Typography sx={{ mt: 1, color: BRAND.dark, fontWeight: 950, letterSpacing: "-.04em", fontSize: { xs: "1.85rem", md: "2.7rem" }, lineHeight: 1.08 }}>
          Armá una cartera posible con precios y porcentajes vigentes.
        </Typography>
        <Typography sx={{ mt: 1.4, color: "rgba(25,36,47,.64)", lineHeight: 1.65 }}>
          Cada cliente representa un proyecto. Elegí cuántos negocios acompañarías y qué servicios tendría cada uno para calcular su mensualidad, el volumen total y la distribución estimada de comisiones.
        </Typography>
      </Box>

      {loading ? (
        <Paper
          elevation={0}
          sx={{ mt: 3.5, p: 3.2, borderRadius: 4, border: "1px solid rgba(25,36,47,.09)", bgcolor: "#fff" }}
        >
          <Stack direction="row" alignItems="center" spacing={1.3}>
            <CircularProgress size={25} sx={{ color: BRAND.orange }} />
            <Typography sx={{ color: "rgba(25,36,47,.62)", fontWeight: 800 }}>
              Preparando el simulador con el catálogo y la política vigentes…
            </Typography>
          </Stack>
        </Paper>
      ) : !hasPrices ? (
        <Alert severity="info" sx={{ mt: 3.5, borderRadius: 3.5 }}>
          El simulador se habilita cuando el sistema publica al menos un servicio con precio vigente. No se usan valores alternativos ni estimaciones inventadas.
        </Alert>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              mt: 3.5,
              p: { xs: 2.3, sm: 3.2 },
              borderRadius: 4.5,
              border: "1px solid rgba(25,36,47,.09)",
              bgcolor: "#fff",
              boxShadow: "0 18px 42px rgba(25,36,47,.055)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0,.7fr) minmax(0,1.3fr)" },
                gap: { xs: 3, lg: 4 },
              }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1.1}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2.7, bgcolor: BRAND.dark, color: "#fff", display: "grid", placeItems: "center" }}>
                    <GroupsRoundedIcon sx={{ fontSize: 23 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>
                      Cantidad de clientes
                    </Typography>
                    <Typography sx={{ color: "rgba(25,36,47,.54)", fontSize: ".78rem" }}>
                      Proyectos con la misma combinación elegida
                    </Typography>
                  </Box>
                </Stack>
                <TextField
                  type="number"
                  value={clients}
                  onChange={(event) => {
                    const next = Math.min(100, Math.max(1, Math.trunc(Number(event.target.value) || 1)));
                    setClients(next);
                    setActivePreset("");
                  }}
                  inputProps={{ min: 1, max: 100, step: 1, inputMode: "numeric" }}
                  fullWidth
                  sx={{ mt: 2.2, "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
                <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.8} sx={{ mt: 1.3 }}>
                  {[1, 3, 5, 10, 20].map((amount) => (
                    <Chip
                      key={amount}
                      clickable
                      label={`${amount} ${amount === 1 ? "cliente" : "clientes"}`}
                      onClick={() => {
                        setClients(amount);
                        setActivePreset("");
                      }}
                      sx={{
                        fontWeight: 800,
                        bgcolor: clients === amount ? "rgba(243,90,58,.12)" : "rgba(25,36,47,.045)",
                        color: clients === amount ? "#C83E21" : BRAND.dark,
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" spacing={1.1}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2.7, bgcolor: "rgba(243,90,58,.1)", color: BRAND.orange, display: "grid", placeItems: "center" }}>
                    <CalculateRoundedIcon sx={{ fontSize: 23 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>
                      Servicios por proyecto
                    </Typography>
                    <Typography sx={{ color: "rgba(25,36,47,.54)", fontSize: ".78rem" }}>
                      Los requisitos de cada servicio se incorporan automáticamente
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    mt: 1.6,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" },
                    gap: 1,
                  }}
                >
                  {services.map((service) => {
                    const checked = selectedCodes.includes(service.code);
                    const requiredBy = selectedServices.filter((selectedService) =>
                      selectedService.requiredServiceCodes.includes(service.code),
                    );

                    return (
                      <Paper
                        key={service.code}
                        elevation={0}
                        sx={{
                          p: 1.25,
                          borderRadius: 3,
                          border: checked
                            ? "1px solid rgba(243,90,58,.38)"
                            : "1px solid rgba(25,36,47,.09)",
                          bgcolor: checked ? "#FFF8F5" : "#fff",
                        }}
                      >
                        <FormControlLabel
                          sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
                          control={(
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleService(service.code)}
                              sx={{ pt: 0.2, color: "rgba(25,36,47,.34)", "&.Mui-checked": { color: BRAND.orange } }}
                            />
                          )}
                          label={(
                            <Box sx={{ minWidth: 0, pt: 0.15 }}>
                              <Typography sx={{ color: BRAND.dark, fontWeight: 900, fontSize: ".9rem", lineHeight: 1.2 }}>
                                {service.name}
                              </Typography>
                              <Typography sx={{ mt: 0.35, color: BRAND.orange, fontWeight: 900, fontSize: ".83rem" }}>
                                {moneyLabel(service.monthlyPrice, service.currency)} / mes
                              </Typography>
                              {requiredBy.length ? (
                                <Typography sx={{ mt: 0.35, color: "rgba(25,36,47,.5)", fontSize: ".68rem", lineHeight: 1.35 }}>
                                  Incluido por {requiredBy.map((item) => item.name).join(", ")}
                                </Typography>
                              ) : null}
                            </Box>
                          )}
                        />
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" },
                gap: 1.5,
              }}
            >
              <MetricCard
                icon={ReceiptLongRoundedIcon}
                eyebrow="Cada negocio factura"
                detail="Suma mensual de los servicios elegidos para un proyecto."
              >
                <MoneyLines values={hasSelection ? perBusiness : []} />
              </MetricCard>
              <MetricCard
                icon={TrendingUpRoundedIcon}
                eyebrow="Volumen mensual"
                detail={`${clients} ${clients === 1 ? "proyecto" : "proyectos"} con la misma combinación.`}
              >
                <MoneyLines values={hasSelection ? monthlyVolume : []} emphasis />
              </MetricCard>
              <MetricCard
                icon={PaymentsRoundedIcon}
                eyebrow="Comisión directa"
                detail={programIsActive && directPercent !== null
                  ? `Calculada con el ${percentLabel(directPercent)} vigente.`
                  : "La política no permite calcular una comisión vigente."}
              >
                <MoneyLines
                  values={hasSelection && programIsActive ? directCommission : []}
                  emptyLabel="No disponible"
                />
              </MetricCard>
              <MetricCard
                icon={AccountTreeRoundedIcon}
                eyebrow="Política por niveles"
                detail={programIsActive
                  ? "Los niveles superiores reciben diferencias, no porcentajes acumulados sin límite."
                  : "La política está pausada o incompleta."}
              >
                <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1.2rem" }}>
                  {programIsActive ? `${levels.length} ${levels.length === 1 ? "nivel visible" : "niveles visibles"}` : "No disponible"}
                </Typography>
              </MetricCard>
            </Box>
          </Paper>

          {programIsActive && levels.length && hasSelection ? (
            <Paper
              elevation={0}
              sx={{ mt: 2.2, p: { xs: 2.2, sm: 3 }, borderRadius: 4.2, border: "1px solid rgba(42,157,103,.18)", bgcolor: "#F4FBF7" }}
            >
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.4}>
                <Box>
                  <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1.08rem" }}>
                    Cómo se distribuye una misma cartera por niveles
                  </Typography>
                  <Typography sx={{ mt: 0.55, color: "rgba(25,36,47,.6)", fontSize: ".83rem", lineHeight: 1.55 }}>
                    El vendedor que origina el negocio conserva el derecho de su profundidad. El referente inmediatamente superior recibe sólo la diferencia con el nivel anterior.
                  </Typography>
                </Box>
                <Chip
                  label={`Tope total de la red: ${percentLabel(program?.levelBehavior?.maximumTotalCommissionPercent)}`}
                  sx={{ alignSelf: { xs: "flex-start", md: "center" }, bgcolor: "rgba(42,157,103,.12)", color: "#157A4A", fontWeight: 900 }}
                />
              </Stack>

              <Box sx={{ mt: 2.2, overflowX: "auto" }}>
                <Box sx={{ minWidth: 680 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "110px minmax(180px,1fr) minmax(180px,1fr) minmax(180px,1fr)",
                      gap: 1,
                      px: 1.2,
                      pb: 1,
                    }}
                  >
                    {["Origen", "Derecho del vendedor", "Comisión sobre el volumen", "Diferencia del superior"].map((label) => (
                      <Typography key={label} sx={{ color: "rgba(25,36,47,.48)", fontSize: ".7rem", fontWeight: 900, textTransform: "uppercase" }}>
                        {label}
                      </Typography>
                    ))}
                  </Box>
                  <Stack spacing={0.8}>
                    {levels.map((level, index) => {
                      const previousEntitlement = index > 0 ? levels[index - 1].entitlementPercent : null;
                      const overridePercent = previousEntitlement === null
                        ? null
                        : Math.max(0, roundMoney(previousEntitlement - level.entitlementPercent));
                      const sellerAmount = commissionTotals(monthlyVolume, level.entitlementPercent);
                      const overrideAmount = overridePercent === null
                        ? []
                        : commissionTotals(monthlyVolume, overridePercent);

                      return (
                        <Box
                          key={level.level}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "110px minmax(180px,1fr) minmax(180px,1fr) minmax(180px,1fr)",
                            gap: 1,
                            alignItems: "center",
                            p: 1.2,
                            borderRadius: 2.5,
                            bgcolor: "#fff",
                            border: "1px solid rgba(25,36,47,.07)",
                          }}
                        >
                          <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>
                            Nivel {level.level}
                          </Typography>
                          <Typography sx={{ color: BRAND.dark, fontWeight: 850 }}>
                            {percentLabel(level.entitlementPercent)}
                          </Typography>
                          <MoneyLines values={sellerAmount} />
                          {overridePercent === null ? (
                            <Typography sx={{ color: "rgba(25,36,47,.5)", fontSize: ".8rem" }}>
                              Venta directa propia
                            </Typography>
                          ) : (
                            <Box>
                              <Typography sx={{ color: BRAND.green, fontWeight: 900, fontSize: ".8rem" }}>
                                {percentLabel(overridePercent)}
                              </Typography>
                              <MoneyLines values={overrideAmount} />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>
            </Paper>
          ) : null}

          <Box sx={{ mt: { xs: 4.5, md: 5.5 } }}>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1.22rem" }}>
              Ejemplos calculados con el catálogo actual
            </Typography>
            <Typography sx={{ mt: 0.7, color: "rgba(25,36,47,.58)", fontSize: ".86rem", lineHeight: 1.55 }}>
              Son puntos de partida editables. Los montos cambian automáticamente cuando cambian los servicios, sus precios o la política del programa.
            </Typography>
            <Box
              sx={{
                mt: 2,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3,minmax(0,1fr))" },
                gap: 1.5,
              }}
            >
              {PRESETS.map((preset) => (
                <ScenarioCard
                  key={preset.key}
                  preset={preset}
                  services={services}
                  program={program}
                  onApply={applyPreset}
                  active={activePreset === preset.key}
                />
              ))}
            </Box>
          </Box>

          <Alert
            severity="warning"
            sx={{ mt: 2.5, borderRadius: 3.5, alignItems: "flex-start", "& .MuiAlert-message": { width: "100%" } }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: ".9rem" }}>
              Estimación comercial, no promesa de ingresos.
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: ".82rem", lineHeight: 1.55 }}>
              Las comisiones se generan únicamente sobre pagos válidos, confirmados y elegibles. Pueden variar por devoluciones, cancelaciones, profundidad real de la red y cambios futuros de la política vigente.
            </Typography>
          </Alert>
        </>
      )}
    </Box>
  );
}
